import React, { useState } from "react";
import { Dropdown, Switch } from "antd";
import {
  CrownOutlined,
  SettingOutlined,
  UploadOutlined,
  UserSwitchOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router";
import logo from "../../assets/icons/logo.png";
import LogoutIcon from "../../assets/icons/logout_dark.svg?react";
import ReleaseNotesIcon from "../../assets/icons/release_alert.svg?react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { useUser } from "@/contexts/UserContext";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { useScopeParams } from "@/hooks/useScopeParams";
import UserMenuHeader from "./UserMenuHeader";
import MyRolesModal from "./MyRolesModal";
import {
  useAssumedRole,
  ROLE_OPTIONS,
  type AssumedRole,
} from "@/contexts/AssumedRoleContext";
import { useAdminFeatureAvailability } from "@/hooks/useAdminNavigation";
import { useTableDensity } from "@/contexts/TableDensityContext";

import { handleLogout } from "../../views/RealHome";

type UserMenuProps = {
  children: React.ReactNode;
  mode?: "navigation" | "default";
};

const withIcon = (Icon: React.FC) => (
  <span className="text-[#20353E]">
    <Icon />
  </span>
);

const withLabelTestId = (label: string, testId: string) => (
  <span data-testid={testId}>{label}</span>
);

export default function UserMenu({ children, mode }: UserMenuProps) {
  const navigate = useNavigate();
  const { scope: scopeParam, id: scopeId, buildUrl } = useScopeParams();
  const { userInfo } = useUser();
  const { isSuperuserMode, setIsSuperuserMode } = useSuperuserMode();
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const [modals, setModals] = useState({
    guide: false,
    announcements: false,
  });

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const handleModal = (modalName: keyof typeof modals, value: boolean) => {
    setModals((prevModals) => ({
      ...prevModals,
      [modalName]: value,
    }));
  };

  const goToWiki = () => {
    window.open("/wiki", "_blank");
  };

  const goToReleaseNotes = () => {
    window.open("/wiki/release-notes/all-release-notes", "_blank");
  };

  const goToFileUpload = () => {
    navigate(buildUrl("file-upload"));
  };

  const goToSuperuserPanel = () => {
    navigate(buildUrl("superuser"));
  };

  const { assumedRole, setAssumedRole } = useAssumedRole();
  const { isCompact, toggleDensity } = useTableDensity();

  const isFileUploadFeatureEnabled = useFeatureFlag(FEATURE_FLAGS.FILE_UPLOAD);
  const { hasAdminAccess } = useAdminFeatureAvailability();
  const isClientAdmin = userInfo?.client_admin;
  const isSuperUser = userInfo?.is_superuser;

  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const openRolesModal = () => {
    setMenuOpen(false);
    setRolesModalOpen(true);
  };

  const viewAsRoleItems: NonNullable<MenuProps["items"]> = [
    {
      key: "assume-role",
      icon: (
        <span
          className="text-[#20353E] inline-flex items-center justify-center"
          style={{ width: 24, height: 24 }}
        >
          <UserSwitchOutlined style={{ fontSize: 18 }} />
        </span>
      ),
      label: (
        <span>
          {assumedRole
            ? ROLE_OPTIONS.find((r) => r.key === assumedRole)?.label
            : "View As Role"}
        </span>
      ),
      children: [
        ...ROLE_OPTIONS.map((role) => ({
          key: `role-${role.key}`,
          icon:
            assumedRole === role.key ? (
              <CheckOutlined style={{ fontSize: 12 }} />
            ) : (
              <span style={{ width: 12, display: "inline-block" }} />
            ),
          label: role.label,
          onClick: () => setAssumedRole(role.key as AssumedRole),
        })),
        { type: "divider" as const },
        {
          key: "role-none",
          icon:
            assumedRole === null ? (
              <CheckOutlined style={{ fontSize: 12 }} />
            ) : (
              <span style={{ width: 12, display: "inline-block" }} />
            ),
          label: "My actual role",
          onClick: () => setAssumedRole(null),
        },
      ],
    },
    {
      type: "divider",
    },
  ];

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: withLabelTestId("Vivo Guide", "user-menu-item-vivo-guide"),
      icon: <img src={logo} width="20px" alt="logo" />,
      onClick: () => goToWiki(),
    },
    {
      key: "3",
      label: withLabelTestId("Release Notes", "user-menu-item-release-notes"),
      icon: withIcon(ReleaseNotesIcon),
      onClick: () => goToReleaseNotes(),
    },
    {
      type: "divider",
    },
    {
      key: "compact-tables",
      icon: (
        <Switch
          className="w-[30px] ml-[-5px]"
          checked={isCompact}
          data-testid="user-menu-compact-tables-switch"
          onChange={(_checked, event) => {
            toggleDensity();
            event.stopPropagation();
          }}
        />
      ),
      label: (
        <div
          className="flex items-center justify-between"
          onClick={(e) => {
            toggleDensity();
            e.stopPropagation();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {withLabelTestId("Compact Tables", "user-menu-item-compact-tables")}
        </div>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
      },
    },
    {
      type: "divider",
    },
    // View As Role is a superuser-only demo control; for everyone else the
    // assumed role is ignored too (see useEffectiveAssumedRole).
    ...(isSuperUser ? viewAsRoleItems : []),
    {
      key: "5",
      label: withLabelTestId("Logout", "user-menu-item-logout"),
      icon: withIcon(LogoutIcon),
      onClick: () => handleLogout(),
    },
  ];

  if (isSuperUser && (scopeParam === "trial" || scopeParam === "portfolio")) {
    items.splice(-2, 0, {
      key: "superuser-mode",
      icon: (
        <Switch
          className="w-[30px] ml-[-5px]"
          checked={isSuperuserMode}
          data-testid="user-menu-superuser-switch"
          onChange={(checked, event) => {
            setIsSuperuserMode(checked);
            event.stopPropagation();
          }}
        />
      ),
      label: (
        <div
          className="flex items-center justify-between"
          onClick={(e) => {
            setIsSuperuserMode(!isSuperuserMode);
            e.stopPropagation();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span>Superuser Mode</span>
        </div>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
      },
    });
  }

  if (
    mode === "navigation" &&
    (scopeParam === "trial" || scopeParam === "portfolio") &&
    hasAdminAccess
  ) {
    items.splice(2, 0, {
      key: "admin",
      label: "Admin Settings",
      icon: (
        <span
          className="text-[#20353E] inline-flex items-center justify-center"
          style={{ width: 24, height: 24 }}
        >
          <SettingOutlined style={{ fontSize: 20 }} />
        </span>
      ),
      onClick: () => navigate("/admin"),
    });
  }

  if (
    isSuperUser &&
    isSuperuserMode &&
    (scopeParam === "trial" || scopeParam === "portfolio")
  ) {
    items.splice(-2, 0, {
      key: "superuser-panel",
      icon: (
        <span
          className="text-[#20353E] inline-flex items-center justify-center"
          style={{ width: 24, height: 24 }}
        >
          <CrownOutlined style={{ fontSize: 20 }} />
        </span>
      ),
      label: withLabelTestId(
        "Superuser Panel",
        "user-menu-item-superuser-panel",
      ),
      onClick: () => goToSuperuserPanel(),
    });
  }

  if (
    mode === "navigation" &&
    scopeParam !== "admin" &&
    isFileUploadFeatureEnabled &&
    (isClientAdmin || isSuperUser)
  ) {
    items.splice(1, 0, {
      key: "file-upload",
      label: "File Upload",
      icon: (
        <span
          className="text-[#20353E] inline-flex items-center justify-center"
          style={{ width: 24, height: 24 }}
        >
          <UploadOutlined style={{ fontSize: 20 }} />
        </span>
      ),
      onClick: () => goToFileUpload(),
    });
  }

  // Prepended here rather than unshifted into `items` above: the conditional
  // blocks splice at fixed indices, which a leading entry would shift.
  const itemsWithHeader: MenuProps["items"] = [
    {
      key: "user-header",
      type: "group",
      label: (
        <UserMenuHeader
          name={`${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim()}
          email={userInfo?.email}
          onOpenRoles={openRolesModal}
        />
      ),
    },
    { type: "divider" },
    ...(items ?? []),
  ];

  return (
    <>
      <Dropdown
        menu={{ items: itemsWithHeader }}
        open={menuOpen}
        onOpenChange={setMenuOpen}
      >
        <div data-testid="user-menu-trigger">{children}</div>
      </Dropdown>
      <MyRolesModal
        open={rolesModalOpen}
        onClose={() => setRolesModalOpen(false)}
        userInfo={userInfo}
        currentTrialId={scopeParam === "trial" ? scopeId : undefined}
      />
    </>
  );
}
